import {
	AD_COMPENSATION_TICK_COUNT,
	BOOST_TICK_DURATION,
	IS_DEV,
	PIGGY_BANK_OFFER_ID,
	PIGGY_BANK_QUEST_ID,
	ROYAL_PROVISION_OFFER_IDS,
} from "@/config/constants";
import { catchUpEventEnd, eventRepository, useEventStore } from "@/event";
import ChestRepository from "@/game/ChestRepository";
import { FrameName } from "@/game/entities/config";
import { QuestObjectiveActions } from "@/game/entities/config/quests/quest_objective_actions";
import { ResourceType } from "@/game/entities/config/resources";
import { UpgradeIds } from "@/game/entities/config/upgrades";
import { EventUpgradeIds } from "@/game/entities/config/upgrades/event";
import QuestRepository from "@/game/QuestRepository";
import SoundService from "@/game/SoundService";
import { ToastPlacement } from "@/game/ToastMessage";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useGameTicker from "@/hooks/useGameTicker";
import useRewardClaimGuard from "@/hooks/useRewardClaimGuard";
import useSegmentManager from "@/hooks/useSegmentManager";
import useSocketService, { EmitStores } from "@/hooks/useSocketService";
import useTileRepository from "@/hooks/useTileRepository";
import Communicator from "@/libs/AppCommunicatior";
import GameError, { GameErrorType } from "@/libs/GameError";
import {
	ClaimableRewardSource,
	type ClaimableRewardsData,
} from "@/libs/SocketService";
import router from "@/router";
import useBuildingsStorageStore from "@/store/useBuildingsStorageStore";
import useOfferStore from "@/store/useOfferStore";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useQuestStore from "@/store/useQuestStore";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import useUserStore from "@/store/useUserStore";
import type { EventGrandPrizeState } from "@/types";
import { EventAdditionalResource } from "@/types/shop";
import { SkinTypes, StatusTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { aggregateClaimableRewardsFromPack } from "./formatPrice";
import {
	getMultiplierResourceType,
	isMultiplier,
	multiplierValue,
	provisionDaysInMs,
} from "./shop/values";

export const initializeFlutterWindowMethods = (): void => {
	const ticker = useGameTicker();
	const socketService = useSocketService();
	const communicator = Communicator.getInstance();

	let watchedAd = false;
	let pendingAdReward = false;

	window.sendVibrationInfo = (message: boolean): void => {
		communicator.setVibrate(message);
	};

	/**
	 * Tato metoda musi byt na FE zadeklarovana a teda FE v nej nastavi ze sa ma zobrazovat v Settings dany button.
	 * Pokym dana metoda nie je zavolana, tak FE nezobrazuje dany button.
	 */
	window.showPrivacyOptionsSettings = (): void => {
		const userStore = useUserStore();
		userStore.setShowPrivacyOptionsSettings(true);
	};

	/**
	 * Tento event Flutter zavola, ak platba prebehla uspesne / neuspesne, aby tuto info posunul dalej na FE.
	 * @param packName Meno balika, ktory sa kupoval
	 * @param isOk ci prebehlo uspesne vo forme stringu "true" alebo "false"
	 */
	window.purchaseFinished = async (
		_,
		isOk: string,
		claimableRewards?: ClaimableRewardsData["claimableRewards"],
		freeClaim?: boolean,
	): Promise<void> => {
		const popupStore = usePopupStore();
		const resourcesStore = useResourcesStore();
		const buildingsStorageStore = useBuildingsStorageStore();
		const upgradesStore = useUpgradesStore();

		if (!claimableRewards)
			claimableRewards = (await socketService.getClaimableRewards())
				.claimableRewards;

		const showPopup = async (isOk: string): Promise<void> => {
			if (isOk !== "true" || !claimableRewards?.length) {
				popupStore.showPopup({
					id: PopupDictionary.PAYMENT_FAILED,
					type: PopupType.INFO,
					heading: translate("ui.payment_failed"),
					body: translate("ui.payment_failed_try_again"),
					skin: SkinTypes.LIGHT,
					isCentered: true,
					isPriority: true,
					dropOnInterrupt: true,
					actions: [
						{
							label: translate("button.continue"),
							buttonType: StatusTypes.INFO,
							callback: async (): Promise<void> => {
								popupStore.hidePopup();

								// Resume ticker after the purchase process, regardless of the purchase process status
								if (!ticker.isLocked) ticker.start();
							},
						},
					],
					closeCallback: (): void => {
						// Resume ticker after the purchase process, regardless of the purchase process status
						if (!ticker.isLocked) ticker.start();
					},
				});

				return;
			}

			if (claimableRewards[0]?.pack?.store_id === PIGGY_BANK_OFFER_ID) {
				useQuestStore().finishQuest(PIGGY_BANK_QUEST_ID);
			}

			const data = claimableRewards.shift();
			const storeId = data?.pack?.store_id;
			const rewards = aggregateClaimableRewardsFromPack(data!.pack);

			if (storeId && ROYAL_PROVISION_OFFER_IDS.includes(storeId)) {
				useUserStore().addProvision(
					storeId,
					provisionDaysInMs(storeId),
				);

				useEmitter().emit(
					EmitterEvents.PhaserRecalculateExplorationSpeed,
				);

				await socketService.emitSaveRequest({
					stores: [EmitStores.USER],
				});
			}

			if (
				data!.pack.store_id ===
				useEventStore().attributes.event_pass_store_id
			) {
				await socketService.emitEventPassPurchased({ status: true });
				await socketService.getEventOverview();
			}

			const claimGuard = useRewardClaimGuard();
			let isClosed = false;
			let isDisabled = false;
			let heading = translate("ui.payment_successful");
			let bodyText = translate("ui.thank_you");
			if (data?.source === ClaimableRewardSource.FREE) {
				heading = translate("ui.free_pack");
				bodyText = translate("ui_special_gift_just_for_you");
			}
			popupStore.showPopup({
				id: PopupDictionary.PAYMENT_SUCCESSFUL,
				type: PopupType.INFO,
				heading: heading,
				body: `<p>${bodyText}</p><p>${data?.pack?.name}</p>`,
				skin: SkinTypes.LIGHT,
				isCentered: true,
				maxHeight: 42, // WARNING: do not change
				isPriority: true,
				actions: [
					{
						label: translate("button.claim"),
						buttonType: StatusTypes.SUCCESS,
						callback: (): Promise<void> =>
							claimGuard.guard(
								async (): Promise<void> => {
									if (isDisabled) return;
									isDisabled = true;

									let eventGrandPrizeState:
										| EventGrandPrizeState
										| undefined;

									(
										Object.entries(rewards) as Array<
											[
												(
													| ResourceType
													| FrameName
													| UpgradeIds
													| string
												),
												number,
											]
										>
									).forEach(
										([reward, value]: [
											(
												| ResourceType
												| FrameName
												| UpgradeIds
												| string
											),
											number,
										]): void => {
											if (
												Object.values(
													ResourceType,
												).includes(
													reward as ResourceType,
												)
											) {
												if (
													isMultiplier(
														reward as ResourceType,
													)
												) {
													resourcesStore.increase(
														getMultiplierResourceType(
															reward as ResourceType,
														),
														multiplierValue(
															reward as ResourceType,
															value,
														),
														"reward claim",
													);
												} else {
													resourcesStore.increase(
														reward as ResourceType,
														value,
														"reward claim",
													);
												}
											}

											if (
												Object.values(
													FrameName,
												).includes(reward as FrameName)
											) {
												buildingsStorageStore.increase(
													reward as FrameName,
													value,
													"reward claim",
												);
											}

											// NOTE: Standard upgrade IDs are already provided,
											// but for event phantom upgrades, we must first attempt to fetch them.
											const customUpgradeId =
												EventUpgradeIds[
													reward as keyof typeof EventUpgradeIds
												] ?? null;

											if (
												Object.values(
													UpgradeIds,
												).includes(+reward) ||
												customUpgradeId
											) {
												const finalUpgradeId = +(
													customUpgradeId ?? reward
												);

												if (!isNaN(finalUpgradeId)) {
													upgradesStore.increaseCurrentLevelById(
														finalUpgradeId,
														true,
													);
													const sm =
														useSegmentManager();
													sm.markAllSegmentsAsDirty();
													sm.run(true); // nech to lagne pri kupe a nie pri ticku
												}
											}

											if (
												Object.values(
													EventAdditionalResource,
												).includes(
													reward as EventAdditionalResource,
												)
											) {
												const store = useEventStore();
												const currentChestKeys =
													store.grandPrizeState
														?.chestKeys ?? 0;
												const chestKeys =
													currentChestKeys + value;

												store.mergeGrandPrizeState({
													chestKeys,
												});
												eventGrandPrizeState = {
													chestKeys,
												};
											}
										},
									);

									await Promise.all([
										socketService.emitSaveRequest({
											stores: [
												EmitStores.BUILDINGS_STORAGE,
												EmitStores.QUESTS,
												EmitStores.UPGRADES,
												EmitStores.RESOURCES,
											],
											eventGrandPrizeState,
											...(freeClaim
												? {}
												: {
														claimedRewards: [
															data!._id,
														],
													}),
										}),
									]);

									isDisabled = false;

									popupStore.hidePopup();

									if (claimableRewards.length) {
										showPopup(isOk);
										return;
									}

									// Resume ticker after the purchase process, regardless of the purchase process status
									if (!ticker.isLocked) ticker.start();
								},
								(): boolean => !isClosed,
							),
					},
				],
				closeCallback: async (): Promise<void> => {
					isClosed = true;
					await socketService.emitLoadRequest({
						stores: [
							// NOTE: load resources that should be updated from the backend after purchase regardless of the state, even if an error occurs (they remain unchanged)
							EmitStores.RESOURCES,
							// NOTE: load premium buildings that should be updated from the backend
							EmitStores.BUILDINGS_STORAGE,
							// NOTE: Load packs from BE again, where the purchase data should already be reflected
							EmitStores.PACKS,
						],
					});

					// Resume ticker after the purchase process, regardless of the purchase process status
					if (!ticker.isLocked) ticker.start();
				},
				rewards,
			});
		};

		showPopup(isOk);
	};

	/**
	 * Toto vola flutter ked hrac ukonci reklamu
	 * VZDY SA VOLA PRED rewardedVideoEarned / (1)
	 */
	window.rewardedVideoEnd = (): void => {
		const runtimeStore = useRuntimeStore();
		const userStore = useUserStore();

		if (!ticker.isLocked) ticker.start();
		SoundService.getInstance().appComeForeground();

		// Earned runs after End — keep offer/chest/boost until it can claim.
		if (watchedAd || pendingAdReward) {
			watchedAd = false;
			return;
		}

		window.setTimeout((): void => {
			if (watchedAd || pendingAdReward) {
				watchedAd = false;
				return;
			}
			userStore.setIsAdPlaying(false);
			useOfferStore().resetOffer();
			if (ChestRepository.getInstance().initialized) {
				ChestRepository.getInstance().removeActiveSpecialChest(false);
			}
			if (runtimeStore.incomeBoostAdActive) {
				runtimeStore.setIncomeBoostActive(false);
				runtimeStore.setIncomeBoostAdActive(false);
				userStore.setIncomeBoostTicksRemaining(0);
			}
		}, 500);
	};

	/**
	 * Tento event Flutter zavola, ak obdrzi z ApplovinMax informaciu - callback - ze rewardedAd bola schovana.
	 * VZDY SA VOLA PO rewardedVideoEnd / (2)
	 */
	window.rewardedVideoEarned = async (): Promise<boolean> => {
		pendingAdReward = true;

		// Minimize drops the socket, wait quietly instead of toast + abort
		while (!(await useRewardClaimGuard().checkConnection(true))) {
			await new Promise<void>((resolve): void => {
				window.setTimeout(resolve, 500);
			});
		}

		const offerStore = useOfferStore();
		const popupStore = usePopupStore();
		const runtimeStore = useRuntimeStore();
		const userStore = useUserStore();
		const resourcesStore = useResourcesStore();

		const chestRepo = ChestRepository.getInstance();

		resourcesStore.increase(ResourceType.ADS_WATCHED_TOTAL, 1, false);
		QuestRepository.getInstance().manageQuestState(
			QuestObjectiveActions.WATCH_ADS,
			null,
			1,
		);

		// If there's no current offer and no active special chest and no income multiplier its offline income - no popup
		if (
			!offerStore.currentOffer &&
			!chestRepo.hasActiveSpecialChest &&
			!runtimeStore.incomeBoostAdActive
		) {
			watchedAd = true;
			pendingAdReward = false;
			useUserStore().setIsAdPlaying(false);
			return true;
		}

		const rewardIcon = offerStore.currentOffer?.icon.includes("clicker")
			? ResourceType.SUPER_CLICKER.toLowerCase()
			: offerStore.currentOffer?.icon;
		const activeSpecialChest = chestRepo.activeSpecialChestReward;
		const reward = activeSpecialChest
			? activeSpecialChest
			: {
					[rewardIcon as string]: offerStore.currentOffer?.amount,
				};

		watchedAd = true;
		const incomeBoostAdActive = runtimeStore.incomeBoostAdActive;
		// Read before hidePopup changes the active popup. Only gate the added compensation.
		const shouldCompensateAd =
			popupStore.state.id !== PopupDictionary.OFFLINE_MORE_AD;

		popupStore.hidePopup();

		// VLADY NOTE: The bonus should be awarded as soon as the thank-you popup appears
		// eslint-disable-next-line no-console
		console.log("VIDEO AD REWARDED!"); // TODO DS testing

		const tickIncome = shouldCompensateAd
			? useTileRepository().calculateMissedTickIncome(
					AD_COMPENSATION_TICK_COUNT,
				)
			: {};

		(Object.entries(tickIncome) as Array<[ResourceType, number]>).forEach(
			([resource, value]: [ResourceType, number]): void => {
				resourcesStore.increase(
					resource,
					value,
					"ad tick compensation",
				);
			},
		);

		if (incomeBoostAdActive) {
			userStore.setIncomeBoostTicksRemaining(BOOST_TICK_DURATION);
			runtimeStore.setIncomeBoostActive(true);
			runtimeStore.setIncomeBoostAdActive(false);
			socketService.emitSaveRequest({
				stores: [EmitStores.USER],
			});
		}
		if (ChestRepository.getInstance().initialized) {
			ChestRepository.getInstance().removeActiveSpecialChest(true);
		}
		if (offerStore.currentOffer) {
			offerStore.applyReward();
		}

		const hasAdRewardToast = (
			Object.entries(reward) as Array<[string, number | undefined]>
		).some(
			([key, value]: [string, number | undefined]): boolean =>
				!!key && typeof value === "number" && value > 0,
		);

		popupStore.showPopup({
			id: PopupDictionary.THANK_YOU_VIDEO_AD,
			type: PopupType.REWARDED_AD,
			heading: translate("ui.rewarded_ad"),
			body: translate("ui.thank_you_notice"),
			skin: SkinTypes.LIGHT,
			isPriority: true,
			dropOnInterrupt: true,
			closeable: false,
			actions: [
				{
					label: translate("button.continue"),
					buttonType: StatusTypes.SUCCESS,
					callback: async (): Promise<void> => {
						popupStore.hidePopup();

						setTimeout((): void => {
							if (Object.keys(tickIncome).length > 0) {
								useEmitter().emit(EmitterEvents.OfflineIncome, {
									reward: tickIncome,
								});
							}

							if (hasAdRewardToast) {
								useEmitter().emit(
									EmitterEvents.PhaserTileClick,
									{
										x: 0,
										y: 0,
										reward,
										placement: ToastPlacement.TOP_CENTER,
										isSpecialChest: !!activeSpecialChest,
										messageNumberOffset:
											Object.keys(tickIncome).length,
									},
								);
							}
						}, 100);
					},
				},
			],
			maxHeight: 13,
			isCentered: true,
		});
		pendingAdReward = false;
		useUserStore().setIsAdPlaying(false);
		return true;
	};

	/**
	 * Tento event Flutter zavola, ak sa nepodarilo reklamu nacitat (callback z ApplovinMax - nenacitalo reklamu)
	 * alebo este nie je nacitana pri spusteni FE
	 * (po schovani Loading Page - Flutter pozrie aktualny stav reklam a posle info - nacitana / nenacitana na FE -
	 * to je poistka pre stav, ak by nahodou reklamu nacitalo pocas loadingu FE a teda FE by este nebol pripraveny resp.
	 * nepocuval na eventy od Flutteru). PAT - FE po zavolani tejto metody neponuka hracovi pozriet reklamu.
	 * Flutter ma timer, ktory sa kazdych 5 minut pokusi o znovu nacitanie reklamy, teda ak nasledne reklama bude uspesne nacitana,
	 * tak potom zavola loadedRewardedAds a FE nasledne zacne hracovi ponukat moznost pozriet reklamu.
	 */
	window.failedToLoadVideoAds = (): void => {
		// const popupStore = usePopupStore();
		// popupStore.showPopup({
		// 	type: PopupType.ERROR,
		// 	heading: translate("ui.rewarded_ad"),
		// 	body: translate("ui.failed_to_load_rewarded_ad"),
		// 	skin: SkinTypes.LIGHT,
		// 	isCentered: true,
		// 	decoratedHeading: "",
		// });
		return;
	};

	/**
	 * Tento event Flutter zavola, ak sa reklama uspesne nacitala
	 */
	window.loadedRewardedAds = (): void => {
		communicator.rewardedAdLoaded();
	};

	/**
	 * Tento event Flutter zavola, pri nacitavani FE - kazdych 5 sekund, aby overil ci nahodou FE
	 * uz nie je nacitany, teda len neposlal event loading done resp. sa jeho poslanie narusilo (ide o poistku).
	 */
	window.isWebLoaded = (): void => {
		if (communicator.getLoadingProgress === 1.0) {
			communicator.loadingDone();
		}
	};

	window.appWentToBackground = (): void => {
		eventRepository.onBackground();
		ticker.pause();
		SoundService.getInstance().appWentBackground();
		socketService.disconnect();
	};

	window.appWentToForeground = (token?: string): void => {
		const foregroundGeneration = eventRepository.onForeground();
		useRuntimeStore().setLastClickTimestamp(null);
		useRuntimeStore().setLastCameraChangeTimestamp(null);
		if (!ticker.isLocked) ticker.resume();
		SoundService.getInstance().appComeForeground();
		if (token) socketService.setFlutterToken(token);
		void socketService
			.connect()
			.then((): void => {
				if (useUserStore().isAdPlaying) {
					// eslint-disable-next-line no-console
					console.log(
						"EE, volas ma ale pozerame este reklamu a vybehla by duplicita",
					); // TODO DS testing
				} else {
					useEmitter().emit(EmitterEvents.GameShowOfflinePopup, true);
				}
				try {
					catchUpEventEnd();
				} catch (error) {
					console.error("Unable to restore event end state:", error);
				}

				// PRIDANÉ: Refresh packov po návrate aplikácie z pozadia do popredia
				try {
					void socketService
						.emitLoadRequest({
							stores: [EmitStores.PACKS],
						})
						.catch((error: unknown): void => {
							console.error("Unable to restore offers:", error);
						});
				} catch (error) {
					console.error("Unable to restore offers:", error);
				}

				// Handles cases where the app was in the background, an event started meanwhile,
				// and event data should be received before the 10s delay completes, followed by a save response.
				void socketService
					.getEventOverview()
					.then((): void => {
						eventRepository.applyForegroundCollectables(
							foregroundGeneration,
						);
					})
					.catch((error: unknown): void => {
						console.error(
							"Unable to restore event collectables:",
							error,
						);
					});
			})
			.catch((error: unknown): void => {
				if (
					error instanceof GameError &&
					error.type ===
						GameErrorType.NETWORK_DISCONNECT_DURING_REQUEST
				) {
					return;
				}
				console.error("Foreground reconnect failed:", error);
			});

		const questStore = useQuestStore();
		if (questStore.tutorialActive && questStore.tutorialStep <= 8) {
			if (
				questStore.activeNarrativeId ||
				questStore.activeNarrativeChapter
			) {
				questStore.stopNarrative();
				questStore.resetQuests();

				// Important: we need to move this back by one since we need to trigger the popup for the current step
				router.push("/"); // To close old windows that were actively open before sleep

				questStore.setTutorialStep(questStore.tutorialStep - 1);
			}
			if (questStore.tutorialStep === 8) {
				// Important: we need to move this back by one since we need to trigger the popup for the current step
				router.push("/"); // To close old windows that were actively open before sleep

				questStore.setTutorialStep(questStore.tutorialStep - 1);
			}
			useEmitter().emit(EmitterEvents.GameInitStoryMode);
		}

		if (useEventStore().isActive) {
			// Handles the case when the app was in the background and, based on the available date-time,
			// we know the timer should have already finished and the event UI needs to be hidden.
			useEmitter().emit(EmitterEvents.EventRecalc);
		}
	};

	// Wrapper for web.
	document.addEventListener("visibilitychange", (): void => {
		if (!IS_DEV) return;
		if (document.visibilityState === "hidden") window.appWentToBackground();
		else window.appWentToForeground();
	});

	/**
	 * Tento event Flutter zavola, ak detekuje, ze na zariadeni bol stlaceny back button / gesture.
	 * Na PAT to funguje tak, ze FE ma na to naviazanu nejaku logiku a nasledne po jej vykonani ->
	 * zavola event do Flutteru ->  exitApp  cim sa aplikacia da do pozadia - teda nekillne sa.
	 */
	window.backButtonFired = (): void => {
		eventRepository.onBackground();
		SoundService.getInstance().appWentBackground();
		ticker.pause();
		socketService.disconnect();
		communicator.exitApp();
	};

	/**
	 * Tento event Flutter zavola po dokonceni BE callu pre linkovanie, su tam rozne variacie,
	 * ktorych podrobny popis bude sucastou dokumentacie pre Prepajania konta.
	 * Ale zatial tu dam zakladny popisok:
	 * linked - toto Flutter posle, ak prepajanie prebehlo uspesne
	 * failed - toto Flutter posle, ak prepajanie prebehlo neuspesne
	 * linkedWithConflict , userId1 - toto Flutter posle, ak dostane z BE odpoved, ze dane konto, uz je prepojene s inym kontom.
	 * Na PAT FE - sa vtedy zobrazi popup, kde hracovi zobrazi info, ze dane konto uz je prepojene s inym - je tam aj zakladne info
	 * ohladom konta, ktore je uz prepojene a tiez konta, ktore este nie je prepojene resp. sucasneho ..
	 * hrac si nasledne moze vybrat, ci chce prepojit akutalne ale s tym, ze sa mu zmaze to uz prepojene
	 * alebo si zvoli to uz prepojene a nasledne sa mu zmaze aktualne a je lognuty do toho uz prepojeneho.
	 */
	window.linkingResult = async (
		state: string,
		userId?: string,
	): Promise<void> => {
		// eslint-disable-next-line no-console
		console.log("Linking result", state, userId);

		const heading = translate("ui.account_linking");
		let body = "";
		let type = "info" as PopupType;

		if (state === "linked") {
			// if state linked = popup linked
			body = translate("ui.link_success");
			await socketService.emitLoadRequest({
				stores: [EmitStores.USER],
			});
			useUserStore().setLinkedEmail(true);
			await socketService.emitSaveRequest({
				stores: [EmitStores.USER],
			});
		} else if (state === "linkedWithConflict") {
			// else if state linkedWithConflict = popup Choose Exist/Override
			type = PopupType.ACCOUNT_LINK_OPTIONS;
			body = userId ?? "";
		} else {
			// else state failed = popup failed
			body = translate("ui.link_failed");
		}

		const popupStore = usePopupStore();
		popupStore.showPopup({
			id: PopupDictionary.ACCOUNT_LINKING,
			type,
			heading,
			body,
			skin: SkinTypes.LIGHT,
			isCentered: true,
			height: 32.5,
		});
	};

	/**
	 * Tento event Flutter zavola, aby poslal info na FE o safe areach. Teda aky je potrebny padding - pre TOP aj pre BOTTOM.
	 * Parametre sa posielaju v pixeloch. Ak zariadenie nema notch resp. nie je nutny padding,
	 * tak sa posle 0, ak je, tak sa posle dana hodnota, napr. 32.0.
	 * Na kolko na niektorych starych Android bol issue, ze nedetekovalo notch, tak sme sa rozhodli
	 * tam dat fallback a teda ak ide o Android a detekovalo ze topSafeAreaPadding je 0.0,
	 * aj napriek tomu ze device realne notch ma, tak sa na FE posle default value - 32.0.
	 * Tento event aktualne Flutter vola viac krat, teda je to viacnasobne zavolanie tejto metody -
	 *  s rovnakymi values - pretoze aktualne je jej zavolanie umiestnene, ak FE posle event - changeLoading.
	 *  Samozrejme je mozne zavolanie daneho eventu prehodit na ine miesto.
	 */
	window.setSafeAreaPadding = (top: number, bottom: number): void => {
		document.documentElement.style.setProperty(
			"--safe-area-inset-top",
			`${top}px`,
		);
		document.documentElement.style.setProperty(
			"--safe-area-inset-bottom",
			`${Math.max(bottom, 20)}px`, // The fallback height must be 20px
		);
	};

	window.setToken = (token: string): void => {
		socketService.setFlutterToken(token);
	};

	/**
	 * Receives deep link data from Flutter and stores it in the runtime store
	 * for later processing in App.vue.
	 *
	 * Flutter calls this via `window.sendDeepLinkData(deepLinkType)` when a deep
	 * link is tapped by the user in the native app. The deep link string is
	 * stored in `runtimeStore.pendingDeepLink` and processed once the game has
	 * finished loading (see App.vue for the processing logic).
	 *
	 * Supported deep link types (see `DeepLinkType` enum in `deepLinkHandler.ts`):
	 * - `welcome-back`       → welcome back popup (before offline income)
	 * - `welcome-back-reward`→ welcome back reward popup (before offline income)
	 * - `welcome-back-offer` → welcome back offer popup (after offline income)
	 * - `event`              → opens active event popup (after offline income)
	 * - `referral`           → opens settings → referral tab (after offline income)
	 * - `stats`              → opens info → stats tab (after offline income)
	 * - `upgrades`           → opens upgrades view (after offline income)
	 * - `market`             → opens shop/market (after offline income)
	 * - `settings`           → opens settings view (after offline income)
	 *

	 */
	window.sendDeepLinkData = (
		deepLinkData: string,
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		_fromNotification?: boolean,
	): void => {
		const runtimeStore = useRuntimeStore();

		// Store the deep link data for processing when the game is ready
		runtimeStore.setPendingDeepLink(deepLinkData);

		// Log for debugging — Flutter may call this at any time, including before
		// the game finishes loading, so we just store it and process later.
		// eslint-disable-next-line no-console
		console.log(`[DeepLink] Received deep link: ${deepLinkData}`);
	};

	/**
	 * Receives referral data from Flutter (separate from deep links).
	 * Flutter calls this with a JSON string containing referral info
	 * (referral code, source, reward details, etc.).
	 * The data is stored in the runtime store and processed when the
	 * user navigates to settings or when the referral deep link is triggered.
	 */
	window.sendReferralData = (referralDataJson: string): void => {
		const runtimeStore = useRuntimeStore();

		try {
			const parsed = JSON.parse(referralDataJson);
			runtimeStore.setReferralData(parsed);
			// eslint-disable-next-line no-console
			console.log("[Referral] Received referral data:", parsed);
		} catch (error) {
			throw new GameError(
				GameErrorType.DATA_ERROR,
				`Failed to parse referral data JSON: ${error instanceof Error ? error.message : error}`,
			);
		}
	};
};
